/* ============================================================================
   Web portal pages
   ----------------------------------------------------------------------------
   /portal             dashboard summary + quick tiles
   /portal/dex         read-only paginated record list
   /portal/upload      bulk Trainer CSV upload
   /portal/account     profile, privacy, delete account

   Design: reuse Nexus Dex tokens (--teal accent, surface-elev), but feel
   denser and dashboard-y. text-xl is the max heading size in app.
   ============================================================================ */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth, backendAssetUrl, type PortalAccount } from "@/components/AuthProvider";
import { trainerDisplayName } from "@/lib/trainerIdentity";
import { validateTrainerLevelInput } from "@/lib/trainerLevel";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useToast } from "@/hooks/use-toast";
import { parseTrainerCsv, sha256OfFile, type ParsedRow } from "@/lib/trainerCsvParser";
import { downloadTrainerCsvTemplate } from "@/lib/trainerCsvTemplate";
import { AuthModal } from "@/components/AuthModal";
import { isTrainerProfileSet } from "@/lib/trainerSetup";
import { authErrorMessage } from "@/lib/apiError";
import { Database, Upload, User as UserIcon, ChevronRight, LogOut, Star, Sparkles, Clover, Download, ShieldCheck, AlertTriangle, Copy, Eye, EyeOff, Check } from "lucide-react";
import { V2PageHeader, V2Accent } from "@/components/v2/V2PageHeader";
import { ADMIN_NAV_GROUPS, adminGroupFor, adminTabIsActive } from "@/lib/adminNav";

// ----------------------------------------------------------------------------
// Email verification banner
// ----------------------------------------------------------------------------

function EmailVerificationBanner() {
  const { account, resendVerification } = useAuth();
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!account || account.emailVerified || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      toast({ title: "Verification email sent", description: `Check your inbox at ${account.email}.` });
    } catch (err: any) {
      toast({ title: "Could not resend", description: authErrorMessage(err, "Try again."), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="px-4 py-2 flex items-center gap-3 text-xs"
      style={{
        background: "hsl(48 80% 50% / 0.15)",
        borderBottom: "1px solid hsl(48 80% 50% / 0.4)",
      }}
      data-testid="banner-email-verify"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "hsl(48 80% 55%)" }} />
      <span className="flex-1">
        Verify your email - we sent a link to{" "}
        <span className="font-mono">{account.email}</span>.
      </span>
      <button
        type="button"
        onClick={handleResend}
        disabled={sending}
        className="font-semibold underline hover:no-underline"
        style={{ color: "hsl(48 80% 55%)" }}
        data-testid="button-resend-verify"
      >
        {sending ? "Sending..." : "Resend"}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-muted-foreground hover:text-foreground ml-1"
        aria-label="Dismiss"
        data-testid="button-dismiss-verify-banner"
      >
        x
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Profile gate - blocks portal access until profile setup is complete
// ----------------------------------------------------------------------------

function ProfileGateScreen() {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "hsl(var(--background) / 0.96)", backdropFilter: "blur(6px)" }}
      data-testid="profile-gate-screen"
    >
      {/* AuthModal in mandatory mode: the trainer step, forceOpen, no "Not now"
          (nothing behind this screen renders until the name is set). */}
      <AuthModal mandatoryMode forceOpen />
    </div>
  );
}

// Centralised gate check - returns true when the signed-in user still needs to
// tell us their trainer name (lib/trainerSetup: the one profile rule; the
// profile screenshot stopped gating anything on 2026-10-02).
function useNeedsProfileSetup(): boolean {
  const { isSignedIn, account } = useAuth();
  if (!isSignedIn || !account) return false;
  return !isTrainerProfileSet(account);
}

// ----------------------------------------------------------------------------
// Layout chrome
// ----------------------------------------------------------------------------

export function PortalShell({ children, active }: { children: React.ReactNode; active?: string }) {
  const { account, signout } = useAuth();
  const [, navigate] = useLocation();

  // The customer-facing portal tabs (Overview, Dex, Upload CSV, Deep Scan)
  // were retired from the nav once the admin section superseded them. Their
  // routes still resolve by direct URL. Account stays for NON-admins so web
  // sign-out / delete-account / privacy remain reachable; for admins it was
  // dropped (2026-08-04) and sign-out lives in the header.
  const isAdmin = !!account?.isAdmin;
  const nonAdminItems = isAdmin ? [] : [{ href: "/portal/account", label: "Account", id: "account" }];

  // Admin nav, in two rows (2026-10-15 owner ask). Twenty-three tabs in one
  // row never fit a laptop, and the pages opened every morning sat beside
  // ones opened once a quarter. Row one is the group (Daily, Analytics,
  // Tools, Reference), row two the tabs of the group the current page is in.
  // The list itself lives in lib/adminNav.ts, shared with the home directory.
  const group = adminGroupFor(active);
  const groupTabs = isAdmin ? group.items : [];

  const tabClass = (on: boolean, size: "sm" | "xs") =>
    `px-3 py-1.5 rounded-md ${size === "sm" ? "text-sm" : "text-xs"} transition whitespace-nowrap shrink-0 ${
      on ? "text-foreground" : "text-muted-foreground hover:text-foreground"
    }`;
  const tabStyle = (on: boolean) => ({ background: on ? "var(--surface-elev-1)" : "transparent" });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <EmailVerificationBanner />
      {/* Push 4 follow-up (v1.4.1 b9): the portal sticky header now pins at
          var(--nx-safe-top) so it lands below the iOS clock /
          Dynamic Island instead of sliding underneath it. Without this,
          QA on every admin page (Captures / Activity / Dataflow / OCR
          debug) reported the header text being clipped at the top by the
          status bar after the contentInset='never' switch. */}
      <div
        aria-hidden
        className="app-shell-safe-spacer"
      />
      <header
        className="sticky z-30 backdrop-blur-md border-b"
        style={{
          background: "hsl(var(--background) / 0.85)",
          borderColor: "hsl(var(--border))",
          top: "var(--nx-safe-top)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
          <Link
            href={isAdmin ? "/portal/admin" : "/portal"}
            className="flex items-center gap-2 hover-elevate rounded-md px-2 py-1"
            data-testid="link-portal-home"
          >
            <Logo size={26} />
            <span className="font-semibold tracking-tight text-sm">Nexus Dex Portal</span>
          </Link>
          {/* Row one: the group pills (admins) or the Account tab (everyone
              else). A group pill opens that group's first page. */}
          <nav
            className="hidden sm:flex items-center gap-1 ml-4 flex-1 min-w-0 overflow-x-auto no-scrollbar"
            aria-label={isAdmin ? "Admin sections" : "Portal"}
            data-testid="portal-nav-groups"
          >
            {nonAdminItems.map((n) => (
              <Link
                key={n.id}
                href={n.href}
                className={tabClass(active === n.id, "sm")}
                style={tabStyle(active === n.id)}
                data-testid={`link-portal-${n.id}`}
              >
                {n.label}
              </Link>
            ))}
            {isAdmin &&
              ADMIN_NAV_GROUPS.map((g) => {
                const on = g.key === group.key;
                return (
                  <Link
                    key={g.key}
                    href={g.items[0].href}
                    className={`${tabClass(on, "sm")} font-medium`}
                    style={{
                      ...tabStyle(on),
                      // The open group carries the accent rule so the two
                      // rows read as one control, not two unrelated bars.
                      boxShadow: on ? "inset 0 -2px 0 var(--accent-fill)" : undefined,
                    }}
                    aria-current={on ? "true" : undefined}
                    data-testid={`link-portal-group-${g.key}`}
                  >
                    {g.label}
                  </Link>
                );
              })}
          </nav>
          <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground shrink-0">
            <span className="truncate max-w-[180px]" data-testid="text-portal-email">
              {account?.email}
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await signout();
              navigate("/portal");
            }}
            data-testid="button-portal-signout"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
        {/* Row two: the open group's tabs. Scrolls sideways on a narrow
            screen rather than wrapping or pushing sign-out off the edge. */}
        {groupTabs.length > 0 && (
          <div className="border-t" style={{ borderColor: "hsl(var(--border))" }}>
            <nav
              className="max-w-6xl mx-auto px-2 sm:px-4 py-1 flex items-center gap-1 overflow-x-auto no-scrollbar"
              aria-label={`${group.label} pages`}
              data-testid="portal-nav-tabs"
            >
              {groupTabs.map((n) => {
                const on = adminTabIsActive(n, active);
                return (
                  <Link
                    key={n.id}
                    href={n.href}
                    className={tabClass(on, "xs")}
                    style={tabStyle(on)}
                    aria-current={on ? "page" : undefined}
                    data-testid={`link-portal-${n.id}`}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
        {/* Mobile: the group pills live on their own row, since the header
            line has no room beside the logo and sign-out. */}
        {(isAdmin || nonAdminItems.length > 0) && (
          <div className="sm:hidden border-t" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="max-w-6xl mx-auto px-2 py-1 flex gap-1 overflow-x-auto">
              {nonAdminItems.map((n) => (
                <Link key={n.id} href={n.href} className={tabClass(active === n.id, "xs")} style={tabStyle(active === n.id)}>
                  {n.label}
                </Link>
              ))}
              {isAdmin &&
                ADMIN_NAV_GROUPS.map((g) => {
                  const on = g.key === group.key;
                  return (
                    <Link
                      key={g.key}
                      href={g.items[0].href}
                      className={`${tabClass(on, "xs")} font-medium`}
                      style={{ ...tabStyle(on), boxShadow: on ? "inset 0 -2px 0 var(--accent-fill)" : undefined }}
                    >
                      {g.label}
                    </Link>
                  );
                })}
            </div>
          </div>
        )}
      </header>
      {/* select-text: index.css turns user-select OFF on <html> for the native
          shell (long-press cleanup, 2026-07-24), which also made every admin
          table unselectable in a desktop browser. Admin pages are a working
          surface: emails get dragged out of one tab and pasted into Compose or
          Play Console, so opt the whole admin content area back in. Non-admin
          portal pages keep the app default. */}
      <main
        className={`max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8${isAdmin ? " select-text" : ""}`}
        data-testid="portal-main"
      >
        {children}
      </main>
    </div>
  );
}

function SignInGate() {
  const { openAuth } = useAuth();
  const [, navigate] = useLocation();
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <div
        className="w-full max-w-md rounded-2xl p-7 text-center"
        style={{
          background: "var(--surface-elev-1)",
          border: "1px solid hsl(var(--border))",
        }}
      >
        <div className="flex justify-center mb-4">
          <Logo size={56} />
        </div>
        <h1 className="text-xl font-bold tracking-tight">Nexus Dex Portal</h1>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
          Sign in to sync across devices, browse your dex on the big screen,
          and bulk-upload Trainer CSVs.
        </p>
        <div className="mt-5 space-y-2">
          <Button
            className="w-full"
            onClick={() => openAuth("signin")}
            data-testid="button-portal-signin"
            style={{ background: "hsl(var(--teal))", color: "white" }}
          >
            Sign in
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => openAuth("signup")}
            data-testid="button-portal-signup"
          >
            Create an account
          </Button>
          {/* b19-ui-header-back-button: back chip removed; global header back button is canonical.
              Spacer preserves the mt-2 plus inline-flex line height the chip occupied. */}
          <div className="mt-2 h-5" aria-hidden />
        </div>
        <p className="mt-5 text-[11px] text-muted-foreground leading-relaxed">
          By trainers, for trainers. We never sell your data, and raw GPS coordinates never
          leave your device.
        </p>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// /portal, dashboard
// ----------------------------------------------------------------------------

interface DexStats {
  total: number;
  shinies: number;
  hundos: number;
  lucky: number;
  lastUploadAt: number | null;
}

export default function PortalHome() {
  const { isSignedIn, account, authedFetch } = useAuth();
  const needsSetup = useNeedsProfileSetup();
  const [, navigate] = useLocation();
  const [stats, setStats] = useState<DexStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Admins land on the Admin section rather than this customer-facing
  // overview — the portal's day-to-day use is admin, and the overview tabs
  // were retired from the nav (see PortalShell). Non-admins still see the
  // overview below.
  useEffect(() => {
    if (isSignedIn && account?.isAdmin) navigate("/portal/admin", { replace: true });
  }, [isSignedIn, account?.isAdmin, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    (async () => {
      try {
        const res = await authedFetch("/api/dex/batches?limit=1");
        if (res.ok) {
          const data = await res.json();
          setStats(data.stats);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [isSignedIn, authedFetch]);

  if (!isSignedIn) return <SignInGate />;
  if (needsSetup) return <ProfileGateScreen />;

  return (
    <PortalShell active="overview">
      <section className="mb-6">
        <V2PageHeader
          eyebrow="PORTAL"
          title={
            <span data-testid="text-portal-welcome">
              Welcome back, <V2Accent>{trainerDisplayName(account)}</V2Accent>.
            </span>
          }
          subtitle="Your full dex, synced across devices. Bulk-upload Trainer CSVs here."
        />
      </section>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatTile label="Total records" value={stats?.total ?? 0} loading={loading} testid="stat-total" />
        <StatTile
          label="Hundos"
          value={stats?.hundos ?? 0}
          loading={loading}
          accent="hsl(var(--perfect))"
          testid="stat-hundos"
        />
        <StatTile
          label="Shinies"
          value={stats?.shinies ?? 0}
          loading={loading}
          accent="hsl(var(--shiny))"
          testid="stat-shinies"
        />
        <StatTile
          label="Lucky"
          value={stats?.lucky ?? 0}
          loading={loading}
          accent="hsl(var(--lucky))"
          testid="stat-lucky"
        />
      </section>

      <p className="text-[11px] text-muted-foreground mb-2">
        Last upload:{" "}
        <span className="font-mono" data-testid="text-last-upload">
          {stats?.lastUploadAt ? new Date(stats.lastUploadAt).toLocaleString() : "never"}
        </span>
      </p>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
        <BigTile href="/portal/dex" icon={Database} title="Browse Dex" desc="Filter, paginate, review." />
        <BigTile
          href="/portal/upload"
          icon={Upload}
          title="Upload CSV"
          desc="Bulk Trainer CSVs."
          accent
        />
        <BigTile href="/portal/account" icon={UserIcon} title="Account" desc="Profile, privacy, delete." />
      </section>
    </PortalShell>
  );
}

function StatTile({
  label,
  value,
  loading,
  accent,
  testid,
}: {
  label: string;
  value: number;
  loading: boolean;
  accent?: string;
  testid?: string;
}) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: "var(--surface-elev-1)",
        border: "1px solid hsl(var(--border))",
      }}
      data-testid={testid}
    >
      <p
        className="text-[10px] uppercase tracking-[0.18em] font-semibold"
        style={{ color: accent ?? "hsl(var(--muted-foreground))" }}
      >
        {label}
      </p>
      <p className="font-mono text-xl font-bold tabular-nums mt-1.5">
        {loading ? "..." : value.toLocaleString()}
      </p>
    </div>
  );
}

function BigTile({
  href,
  icon: Icon,
  title,
  desc,
  accent,
}: {
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  desc: string;
  accent?: boolean;
}) {
  return (
    <Link href={href}>
      <a
        className="group rounded-xl p-5 block transition hover-elevate"
        style={{
          background: accent
            ? "linear-gradient(135deg, hsl(var(--teal) / 0.18), var(--surface-elev-1) 80%)"
            : "var(--surface-elev-1)",
          border: `1px solid ${accent ? "hsl(var(--teal) / 0.4)" : "hsl(var(--border))"}`,
        }}
        data-testid={`tile-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="flex items-start justify-between">
          <Icon className="w-5 h-5" style={{ color: accent ? "hsl(var(--teal))" : "hsl(var(--foreground))" }} />
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition" />
        </div>
        <p className="font-semibold mt-3">{title}</p>
        <p className="text-xs text-muted-foreground mt-1">{desc}</p>
      </a>
    </Link>
  );
}

// ----------------------------------------------------------------------------
// /portal/dex, paginated read-only list
// ----------------------------------------------------------------------------

interface DexRecord {
  id: number;
  speciesId: number;
  formId: string | null;
  isShiny: boolean;
  isLucky: boolean;
  isShadow: boolean;
  isPurified: boolean;
  ivAtk: number | null;
  ivDef: number | null;
  ivSta: number | null;
  cp: number | null;
  hp: number | null;
  level: number | null;
  nickname: string | null;
  portraitUrl: string | null;
}

type DexFilter = "all" | "shiny" | "hundo" | "lucky";

export function PortalDex() {
  const { isSignedIn, authedFetch } = useAuth();
  const needsSetup = useNeedsProfileSetup();
  const [filter, setFilter] = useState<DexFilter>("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: DexRecord[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSignedIn) return;
    setLoading(true);
    (async () => {
      const res = await authedFetch(`/api/dex/records?page=${page}&perPage=200`);
      if (res.ok) setData(await res.json());
      setLoading(false);
    })();
  }, [isSignedIn, authedFetch, page]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => {
      if (filter === "shiny") return r.isShiny;
      if (filter === "lucky") return r.isLucky;
      if (filter === "hundo") return r.ivAtk === 15 && r.ivDef === 15 && r.ivSta === 15;
      return true;
    });
  }, [data, filter]);

  if (!isSignedIn) return <SignInGate />;
  if (needsSetup) return <ProfileGateScreen />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 200)) : 1;

  return (
    <PortalShell active="dex">
      <V2PageHeader
        eyebrow="PORTAL"
        title={<>Your <V2Accent>dex</V2Accent>.</>}
        subtitle="Read-only view of synced records. Filter and browse."
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {(["all", "shiny", "hundo", "lucky"] as DexFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              filter === f ? "text-foreground" : "text-muted-foreground"
            }`}
            style={{
              background: filter === f ? "hsl(var(--teal) / 0.18)" : "var(--surface-elev-1)",
              border: `1px solid ${filter === f ? "hsl(var(--teal) / 0.5)" : "hsl(var(--border))"}`,
            }}
            data-testid={`chip-filter-${f}`}
          >
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {!loading && data && data.total === 0 && (
        <div
          className="rounded-xl p-6 text-center"
          style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
        >
          <Database className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="text-sm font-semibold mt-2">No records yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Upload a Trainer CSV from the Upload tab to populate your dex.
          </p>
        </div>
      )}
      {!loading && data && data.total > 0 && (
        <>
          <div
            className="rounded-xl overflow-hidden"
            style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
          >
            <table className="w-full text-xs">
              <thead className="bg-[var(--surface-elev-2)] text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 w-12">Art</th>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Nickname</th>
                  <th className="text-right px-3 py-2">CP</th>
                  <th className="text-right px-3 py-2">IV A/D/S</th>
                  <th className="text-right px-3 py-2">Lvl</th>
                  <th className="text-left px-3 py-2">Tags</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isHundo = r.ivAtk === 15 && r.ivDef === 15 && r.ivSta === 15;
                  return (
                    <tr
                      key={r.id}
                      className="border-t"
                      style={{ borderColor: "hsl(var(--border))" }}
                      data-testid={`row-record-${r.id}`}
                    >
                      <td className="px-2 py-1.5">
                        <DexPortraitCell record={r} />
                      </td>
                      <td className="px-3 py-2 font-mono">{r.speciesId}</td>
                      <td className="px-3 py-2">{r.nickname ?? "-"}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.cp ?? "-"}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {r.ivAtk ?? "?"}/{r.ivDef ?? "?"}/{r.ivSta ?? "?"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.level ?? "-"}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {r.isShiny && <Tag color="hsl(var(--shiny))" icon={Sparkles} label="Shiny" />}
                          {r.isLucky && <Tag color="hsl(var(--lucky))" icon={Clover} label="Lucky" />}
                          {isHundo && <Tag color="hsl(var(--perfect))" icon={Star} label="Hundo" />}
                          {r.isShadow && <Tag color="hsl(0 70% 60%)" label="Shadow" />}
                          {r.isPurified && <Tag color="hsl(48 80% 60%)" label="Purified" />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                      No matches in this view.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between text-xs">
            <p className="text-muted-foreground" data-testid="text-record-total">
              Showing {filtered.length} of {data.total} records
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="button-page-prev"
              >
                Previous
              </Button>
              <span className="font-mono text-muted-foreground self-center">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="button-page-next"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </PortalShell>
  );
}

// Inline portrait cell for the dex table. Shows the live-scan portrait when
// present and otherwise opens a tap-to-upload sheet from the photo library.
function DexPortraitCell({ record }: { record: DexRecord }) {
  const { authedFetch } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(record.portraitUrl);

  const onPick = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("portrait", file);
      const res = await authedFetch(`/api/pokemon-records/${record.id}/portrait`, {
        method: "POST",
        body: form,
      });
      if (res.ok) {
        const body = await res.json();
        const next = body?.record?.portraitUrl ?? body?.portraitUrl ?? null;
        if (next) setUrl(next);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => fileRef.current?.click()}
      className="relative rounded overflow-hidden block"
      style={{
        width: 36,
        height: 36,
        background: "var(--surface-elev-2)",
        border: "1px solid hsl(var(--border))",
      }}
      title={url ? "Replace portrait" : "Add portrait"}
      data-testid={`button-row-portrait-${record.id}`}
      disabled={busy}
    >
      {url ? (
        <img
          src={backendAssetUrl(url)}
          alt=""
          className="w-full h-full object-cover"
          data-testid={`img-row-portrait-${record.id}`}
        />
      ) : (
        <span
          className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground"
          aria-hidden
        >
          {busy ? "..." : "+"}
        </span>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        data-testid={`input-row-portrait-${record.id}`}
      />
    </button>
  );
}

function Tag({
  color,
  icon: Icon,
  label,
}: {
  color: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{ background: `${color} / 0.18`, color, border: `1px solid ${color}` }}
    >
      {Icon && <Icon className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

// ----------------------------------------------------------------------------
// /portal/upload, bulk CSV
// ----------------------------------------------------------------------------

export function PortalUpload() {
  const { isSignedIn, authedFetch, account } = useAuth();
  const needsSetup = useNeedsProfileSetup();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<{ rows: ParsedRow[]; errors: { rowIndex: number; reason: string }[] } | null>(null);
  const [contentHash, setContentHash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [result, setResult] = useState<null | {
    deduped: boolean;
    insertedRecords?: number;
    message?: string;
  }>(null);

  if (!isSignedIn) return <SignInGate />;
  if (needsSetup) return <ProfileGateScreen />;

  const onFile = async (f: File | null) => {
    setFile(f);
    setParsed(null);
    setContentHash(null);
    setResult(null);
    if (!f) return;
    setProgress("Hashing file...");
    const hash = await sha256OfFile(f);
    setContentHash(hash);
    setProgress("Parsing CSV...");
    const text = await f.text();
    const out = parseTrainerCsv(text);
    setParsed(out);
    setProgress(null);
  };

  const upload = async () => {
    if (!parsed || !contentHash) return;
    setBusy(true);
    setProgress(`Uploading ${parsed.rows.length} rows...`);
    try {
      const res = await authedFetch("/api/csv-upload", {
        method: "POST",
        body: JSON.stringify({
          rows: parsed.rows,
          contentHash,
          surface: "web",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setResult(data);
      if (data.deduped) {
        toast({
          title: "Already uploaded",
          description: "This exact file was uploaded before, no rows added.",
        });
      } else {
        toast({
          title: "Upload complete",
          description: `${data.insertedRecords} records added to your dex.`,
        });
      }
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message ?? "Try again.", variant: "destructive" });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const preview = parsed?.rows.slice(0, 10) ?? [];

  return (
    <PortalShell active="upload">
      <V2PageHeader
        eyebrow="PORTAL"
        title={<>Upload <V2Accent>Trainer CSV</V2Accent>.</>}
        subtitle="Bulk import a Trainer CSV from any tracker, or your own offline spreadsheet matching the Nexus Dex template. Re-uploading the same file is a no-op (we dedupe by hash)."
      />

      <div className="mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadTrainerCsvTemplate()}
          className="gap-2"
          data-testid="button-download-template"
        >
          <Download className="w-3.5 h-3.5" />
          Download blank template
        </Button>
        <p className="text-[11px] text-muted-foreground mt-2">
          Opens cleanly in Excel or Google Sheets. Fill it offline, save as CSV, then upload.
        </p>
      </div>

      <div
        className="rounded-xl p-3 mb-4 flex gap-3 items-start"
        style={{
          background: "hsl(var(--teal) / 0.10)",
          border: "1px solid hsl(var(--teal) / 0.3)",
        }}
        data-testid="card-privacy-badge"
      >
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
          style={{ background: "hsl(var(--teal) / 0.2)" }}
        >
          <span className="text-xs font-bold" style={{ color: "hsl(var(--teal))" }}>
            ~80m
          </span>
        </div>
        <div className="text-xs leading-relaxed">
          <p className="font-semibold">Privacy: raw lat/long never leaves your device.</p>
          <p className="text-muted-foreground mt-0.5">
            We bucket coordinates to ~80m before upload, then drop the raw values from the row.
          </p>
        </div>
      </div>

      <div
        className="rounded-xl p-5"
        style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
      >
        <Input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          data-testid="input-csv-file"
        />
        {progress && <p className="text-xs text-muted-foreground mt-3" data-testid="text-progress">{progress}</p>}
        {parsed && (
          <div className="mt-4">
            <p className="text-xs">
              <span className="font-semibold" data-testid="text-row-count">{parsed.rows.length}</span> rows parsed
              {parsed.errors.length > 0 && (
                <span className="text-destructive">, {parsed.errors.length} skipped</span>
              )}
              .
            </p>
            {contentHash && (
              <p className="text-[10px] text-muted-foreground font-mono mt-1 truncate">
                hash: {contentHash.slice(0, 16)}...
              </p>
            )}

            {preview.length > 0 && (
              <div
                className="mt-3 rounded-lg overflow-hidden"
                style={{ border: "1px solid hsl(var(--border))" }}
              >
                <table className="w-full text-[11px]">
                  <thead className="bg-[hsl(var(--muted))] text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-1">#</th>
                      <th className="text-left px-2 py-1">Name</th>
                      <th className="text-right px-2 py-1">CP</th>
                      <th className="text-right px-2 py-1">A/D/S</th>
                      <th className="text-left px-2 py-1">Tags</th>
                      <th className="text-left px-2 py-1">Geo (bucketed)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: "hsl(var(--border))" }}>
                        <td className="px-2 py-1 font-mono">{r.speciesId}</td>
                        <td className="px-2 py-1">{r.nickname ?? "-"}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.cp ?? "-"}</td>
                        <td className="px-2 py-1 text-right font-mono">
                          {r.ivAtk ?? "?"}/{r.ivDef ?? "?"}/{r.ivSta ?? "?"}
                        </td>
                        <td className="px-2 py-1">
                          {[r.isShiny && "Shiny", r.isLucky && "Lucky", r.isShadow && "Shadow", r.isPurified && "Purified"]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </td>
                        <td className="px-2 py-1 font-mono text-[10px]">{r.geoS2L17 ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 10 && (
                  <p className="text-[10px] text-muted-foreground p-2">
                    Preview of first 10 rows of {parsed.rows.length}.
                  </p>
                )}
              </div>
            )}

            <Button
              onClick={upload}
              disabled={busy || parsed.rows.length === 0}
              className="mt-4"
              style={{ background: "hsl(var(--teal))", color: "white" }}
              data-testid="button-csv-upload"
            >
              {busy ? "Uploading..." : `Upload ${parsed.rows.length} rows`}
            </Button>
          </div>
        )}

        {result && (
          <div
            className="mt-4 rounded-lg p-3 text-xs"
            style={{
              background: result.deduped ? "hsl(48 80% 60% / 0.1)" : "hsl(var(--teal) / 0.1)",
              border: `1px solid ${result.deduped ? "hsl(48 80% 60% / 0.4)" : "hsl(var(--teal) / 0.4)"}`,
            }}
            data-testid="card-upload-result"
          >
            {result.deduped ? (
              <>
                <p className="font-semibold">Already uploaded</p>
                <p className="text-muted-foreground">
                  This exact file matched a previous upload, no rows ingested.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">Upload complete</p>
                <p className="text-muted-foreground">
                  {result.insertedRecords} records added.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </PortalShell>
  );
}

// ----------------------------------------------------------------------------
// /portal/account, profile + delete
// ----------------------------------------------------------------------------

export function PortalAccount() {
  const { isSignedIn, account, patchMe, deleteAccount, signout, logoutAll, resendVerification, authedFetch } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [fcRevealed, setFcRevealed] = useState(false);
  const [fcCopied, setFcCopied] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  // Trainer level input state (v1.1.3-b3). Local string so the input can be
  // edited freely; persisted via PATCH /api/auth/me on Save.
  const [levelInput, setLevelInput] = useState<string>(
    account?.trainerLevel != null ? String(account.trainerLevel) : "",
  );
  const [levelBusy, setLevelBusy] = useState(false);
  useEffect(() => {
    // Re-sync if a different account loads or the OCR import populates the
    // level after this component mounted.
    setLevelInput(account?.trainerLevel != null ? String(account.trainerLevel) : "");
  }, [account?.trainerLevel]);

  if (!isSignedIn || !account) return <SignInGate />;
  // Note: PortalAccount is exempt from the gate so users can sign out.
  // The gate still applies to all other portal pages.

  const onDelete = async () => {
    if (confirmText !== "delete") return;
    setBusy(true);
    try {
      await deleteAccount();
      toast({ title: "Account deleted", description: "Your records have been removed." });
      await signout();
      navigate("/portal");
    } catch (err: any) {
      toast({ title: "Could not delete", description: authErrorMessage(err, "Try again."), variant: "destructive" });
      setBusy(false);
    }
  };

  const memberSince = account.createdAt
    ? new Date(account.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : "-";

  const onLogoutAll = async () => {
    setLogoutBusy(true);
    try {
      await logoutAll();
      toast({ title: "Signed out everywhere" });
      navigate("/portal");
    } catch (err: any) {
      toast({ title: "Could not sign out", description: err?.message ?? "Try again.", variant: "destructive" });
      setLogoutBusy(false);
    }
  };

  const onExport = async () => {
    setExportBusy(true);
    try {
      const res = await authedFetch("/api/account/export", { method: "GET" });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `nexus-dex-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "Data exported", description: "Your account snapshot has been downloaded." });
    } catch (err: any) {
      toast({ title: "Could not export", description: err?.message ?? "Try again.", variant: "destructive" });
    } finally {
      setExportBusy(false);
    }
  };

  const onResendVerify = async () => {
    setResendBusy(true);
    try {
      await resendVerification();
      toast({ title: "Verification email sent", description: `Check your inbox at ${account.email}.` });
    } catch (err: any) {
      toast({ title: "Could not resend", description: authErrorMessage(err, "Try again."), variant: "destructive" });
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <PortalShell active="account">
      <V2PageHeader
        eyebrow="PORTAL"
        title={<>Your <V2Accent>account</V2Accent>.</>}
        subtitle="Profile, privacy, research-mode, and danger zone."
      />

      {/* Identity section */}
      <section
        className="rounded-xl p-5 mb-4"
        style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
      >
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Email</p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="font-mono text-xs" data-testid="text-account-email">{account.email}</p>
              {account.emailVerified ? (
                <span className="flex items-center gap-1 text-[10px]" style={{ color: "hsl(var(--teal))" }}>
                  <ShieldCheck className="w-3 h-3" /> verified
                </span>
              ) : (
                <button
                  type="button"
                  onClick={onResendVerify}
                  disabled={resendBusy}
                  className="text-[10px] underline"
                  style={{ color: "hsl(48 80% 55%)" }}
                  data-testid="button-resend-verify-account"
                >
                  {resendBusy ? "Sending..." : "Verify"}
                </button>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Member since</p>
            <p className="mt-0.5 text-sm" data-testid="text-account-since">{memberSince}</p>
          </div>
          {account.username && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Username</p>
              <p className="font-mono mt-0.5 text-xs" data-testid="text-account-username">@{account.username}</p>
            </div>
          )}
          {account.trainerName && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Trainer name</p>
              <p className="mt-0.5 text-sm" data-testid="text-account-trainer-name">{account.trainerName}</p>
            </div>
          )}
          {account.friendCode && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Friend code</p>
              <div className="flex items-center gap-2 mt-0.5" data-testid="friend-code-reveal-row">
                <p
                  className="font-mono text-xs select-none"
                  data-testid="text-account-friend-code"
                  style={{ letterSpacing: "0.08em" }}
                >
                  {fcRevealed
                    ? account.friendCode.replace(/^(\d{4})(\d{4})(\d{4})$/, "$1 $2 $3")
                    : "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022"}
                </p>
                {!fcRevealed ? (
                  <button
                    type="button"
                    onClick={() => setFcRevealed(true)}
                    className="flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 transition"
                    style={{
                      color: "hsl(var(--teal))",
                      background: "hsl(var(--teal) / 0.1)",
                      border: "1px solid hsl(var(--teal) / 0.3)",
                    }}
                    data-testid="button-reveal-friend-code"
                    aria-label="Reveal friend code"
                  >
                    <Eye className="w-3 h-3" /> Reveal
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          account.friendCode!.replace(/^(\d{4})(\d{4})(\d{4})$/, "$1 $2 $3")
                        ).then(() => {
                          setFcCopied(true);
                          setTimeout(() => setFcCopied(false), 2000);
                        });
                      }}
                      className="flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 transition"
                      style={{
                        color: fcCopied ? "hsl(142 60% 45%)" : "hsl(var(--teal))",
                        background: "hsl(var(--teal) / 0.1)",
                        border: "1px solid hsl(var(--teal) / 0.3)",
                      }}
                      data-testid="button-copy-friend-code"
                      aria-label="Copy friend code"
                    >
                      {fcCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {fcCopied ? "Copied" : "Copy"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFcRevealed(false)}
                      className="flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 transition text-muted-foreground"
                      style={{
                        background: "var(--surface-elev-1)",
                        border: "1px solid hsl(var(--border))",
                      }}
                      data-testid="button-hide-friend-code"
                      aria-label="Hide friend code"
                    >
                      <EyeOff className="w-3 h-3" /> Hide
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

      </section>

      {/* Trainer level (v1.1.3-b3). Free for every tier; no Pro gate. */}
      <section
        className="rounded-xl p-5 mb-4"
        style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-semibold">Trainer level</p>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
              Your Pokemon GO level. We use this to personalise tips. Range 1 to 80.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="trainer-level-input" className="sr-only">Trainer level</Label>
            <Input
              id="trainer-level-input"
              type="number"
              inputMode="numeric"
              min={1}
              max={80}
              step={1}
              className="w-20"
              value={levelInput}
              onChange={(e) => setLevelInput(e.target.value)}
              placeholder="1-80"
              data-testid="input-trainer-level"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={levelBusy}
              onClick={async () => {
                const validation = validateTrainerLevelInput(levelInput);
                if (!validation.ok) {
                  toast({ title: "Out of range", description: validation.reason, variant: "destructive" });
                  return;
                }
                const n = validation.value;
                setLevelBusy(true);
                try {
                  await patchMe({ trainer_level: n });
                  toast({
                    title: n == null ? "Cleared" : "Saved",
                    description: n == null ? "Trainer level removed." : `Trainer level set to ${n}.`,
                  });
                } catch (err: any) {
                  toast({ title: "Could not save", description: authErrorMessage(err, "Try again."), variant: "destructive" });
                } finally {
                  setLevelBusy(false);
                }
              }}
              data-testid="button-save-trainer-level"
            >
              {levelBusy ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </section>

      {/* Data export (GDPR Article 20) */}
      <section
        className="rounded-xl p-5 mb-4"
        style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm font-semibold">Download my data</p>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
              Get a JSON snapshot of your account, dex records, friends, and settings.
              You can take this with you any time, that is your right.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={exportBusy}
            data-testid="button-export-data"
            className="gap-2"
          >
            <Download className="w-3.5 h-3.5" />
            {exportBusy ? "Preparing..." : "Export JSON"}
          </Button>
        </div>
      </section>

      <section
        className="rounded-xl p-5 border-2"
        style={{
          background: "hsl(0 70% 50% / 0.06)",
          borderColor: "hsl(0 70% 50% / 0.4)",
        }}
      >
        <p className="text-sm font-semibold" style={{ color: "hsl(0 70% 60%)" }}>
          Delete account
        </p>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          This removes your account and every record tied to it. This
          cannot be undone.
        </p>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder='Type "delete" to confirm'
          data-testid="input-delete-confirm"
          className="mb-3"
        />
        <Button
          variant="destructive"
          disabled={confirmText !== "delete" || busy}
          onClick={onDelete}
          data-testid="button-delete-account"
        >
          {busy ? "Deleting..." : "Delete my account"}
        </Button>
      </section>

      {/* Sign out everywhere */}
      <section
        className="rounded-xl p-5 mb-4"
        style={{ background: "var(--surface-elev-1)", border: "1px solid hsl(var(--border))" }}
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold">Sign out everywhere</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Revokes all active sessions on every device. Useful if you think your account was accessed from a shared device.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onLogoutAll}
            disabled={logoutBusy}
            data-testid="button-logout-all"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" />
            {logoutBusy ? "Signing out..." : "Sign out all"}
          </Button>
        </div>
      </section>
    </PortalShell>
  );
}
